/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { addScormResponseAction } from 'actions/addScormResponseAction';
import { updateScoreAction } from 'actions/updateScoreAction';
import { cloneElement, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useScoringQuizzes } from 'services/apiHelpers/useScoringQuizzes';
import hexToRgb from 'services/hexToRGB';
import pubSub from 'services/pubSub';
import { unlockInteraction } from 'services/unlockInteraction';
import { Report } from 'store/store';
import { interactionContext } from './interactionContext';

import { HostingContext } from 'hosting/context';
import { sliderSubmitEvent } from 'hosting/events';

export const defaultColor = '#845cff';

const FromToField = props => {
  const { field } = props;

  const { content } = field?.props;
  const alignment =
    !content || content === '<p data-type="paragraph"></p>' ? 'left' : 'center';

  return (
    <div
      css={css({
        width: 320 / 2,
        margin: `0 0 0 ${window.dev() ? -10 : 0}`,

        textAlign: alignment,
      })}
    >
      {field}
    </div>
  );
};

export type Result = 'correct' | 'incorrect' | 'attempt' | undefined;
enum SubmitState {
  Initial = 0,
  Resolved = 2,
}

const Slider = props => {
  const InteractionContext = useContext(interactionContext);
  const hostingCtx = useContext(HostingContext);

  const { id } = InteractionContext;

  let {
    sliderMin,
    sliderMax,
    sliderStartValue,
    sliderTrackColor = defaultColor,
    sliderTrackOpacity = 60,
    sliderHandleColor = defaultColor,
    showSliderStepMarkers = true,
    sliderStepMarkerColor = defaultColor,
    numberOfSliderSteps = 10,
    sliderAnswerFormat = 'fixed',
    includeSliderAnswerInputBox = true,
    SliderAnswerTextColor = '#000',
    sliderContentColor,
    sliderInputBoxBackgroundColor = '#fff',
    sliderInputBoxBackgroundOpacity = 100,
    sliderInputBoxOutline = true,
    sliderInputBoxOutlineColor = defaultColor,

    //
    lockDown = 0,
    numberOfAttempts = 2,
    showAttemptNumber = true,
    useMediaInFeedback = true,
    // correctAnswer,
    showCorrectAnswer = true,
    navigationBehaviour = {
      correct: false,
      incorrect: false,
    },
    score,
    includeFeedback = true,
    includeQuestionTextPlusImage,
  } = InteractionContext.props;

  if (sliderMin === undefined) {
    sliderMin = 0;
  }

  if (sliderMax === undefined) {
    sliderMax = 10;
  }

  if (!sliderStartValue) {
    sliderStartValue = sliderMin;
  }

  const scoringQuizzes = useScoringQuizzes();

  let correctAnswer = InteractionContext.props.correctAnswer || sliderMin;

  // console.log({ sliderMin, sliderMax, correctAnswer });

  const { children } = props;

  const { r, g, b } = hexToRgb(sliderTrackColor);
  const a = sliderTrackOpacity * 0.01;
  const rgba = `rgba(${r}, ${g}, ${b}, ${a})`;

  const { r: sliderInputR, g: sliderInputG, b: sliderInputB } = hexToRgb(
    sliderInputBoxBackgroundColor
  );
  const sliderInputA = sliderInputBoxBackgroundOpacity * 0.01;
  sliderInputBoxBackgroundColor = `rgba(${sliderInputR}, ${sliderInputG}, ${sliderInputB}, ${sliderInputA})`;

  const sliderHandleSize = 35;

  const getSliderHandleColor = (replaceHash = false) => {
    return !replaceHash
      ? sliderHandleColor
      : sliderHandleColor.replace('#', '%23');
  };
  const sliderHandle = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="${sliderHandleSize}" height="${sliderHandleSize}" viewBox="0 0 ${sliderHandleSize} ${sliderHandleSize}"><circle r="${sliderHandleSize /
    2}" cx="${sliderHandleSize / 2}" cy="${sliderHandleSize /
    2}" fill="${getSliderHandleColor(true)}" />
    
    <circle r="${sliderHandleSize / 3}" cx="${sliderHandleSize /
    2}" cy="${sliderHandleSize / 2}" fill="black" fill-opacity="0.15" />

    </svg>')`;

  //
  const [value, setValue] = useState<number | undefined>();
  const [inputFieldHasFocus, setInputFieldHasFocus] = useState<boolean>(false);
  const [attempt, setAttempt] = useState<number>(0);
  const [result, setResult] = useState<Result>(undefined);
  const [resolved, setResolved] = useState<SubmitState>(0);
  const [showFeedback, setShowFeedback] = useState<Result>(result);
  const [isLive, setIsLive] = useState<boolean | undefined>(undefined);
  const dispatch = useDispatch();

  const [sharedQuestionMedia, from, to, feedback] = children;
  const [, , , submitBtn] = feedback?.props.children;

  const _value_ = value || sliderStartValue;

  const onSubmit = () => {
    let result: Result;
    if (value == correctAnswer) {
      result = 'correct';
    }

    hostingCtx?.data(
      sliderSubmitEvent(id, {
        value: _value_,
        success: result === 'correct',
        attemptNumber: attempt + 1,
      })
    );

    // logic for attempts...
    if (result !== 'correct') {
      if (attempt < numberOfAttempts - 1) {
        setAttempt(attempt => {
          return (attempt += 1);
        });
        result = 'attempt';
      } else {
        result = 'incorrect';
      }
    }

    setResult(result);
    setShowFeedback(result);
  };

  useEffect(() => {
    if ((['correct', 'incorrect'] as Result[]).includes(result)) {
      setResolved(SubmitState.Resolved);
      //

      let question;

      const sliderElem = document.querySelector(
        `#i${InteractionContext.id}`
      ) as HTMLElement;

      let questionElement = sliderElem?.querySelector(
        `.chameleon-text :not([class*="FromToField"])`
      ) as HTMLElement;

      if (questionElement) {
        question = questionElement.innerText;
      }

      const fromToFields = Array.from(
        sliderElem?.querySelectorAll(
          `#i${InteractionContext.id} [class*="FromToField"]`
        )
      ) as HTMLElement[];

      if (fromToFields) {
        question = `${question && question + ' - '}${
          fromToFields[0].innerText
        }/${fromToFields[1].innerText}`;
      }

      if (question) {
        question = question
          ?.replace(/\n/g, '')
          ?.replace(/\s+/g, ' ')
          ?.trim();
      }

      const action = {
        id: InteractionContext.id,
        type: 'likert',
        question,
        reportable: 'likert' as 'likert',
        response: String(value),
        correct: String(correctAnswer),
        result: (result === 'correct' ? 1 : 0) as Report['result'],
      };

      // dispatch if there's no scoringQuizzes etc
      let shouldDispatchScore = score;

      if (scoringQuizzes.length) {
        shouldDispatchScore = false;
      }
      //

      if (shouldDispatchScore) {
        dispatch(
          updateScoreAction(InteractionContext.id, result === 'correct' ? 1 : 0)
        );
      }

      dispatch(addScormResponseAction(action));

      const unlock = result === 'correct' || attempt === numberOfAttempts - 1;

      if (unlock) {
        unlockInteraction(InteractionContext.id);
        setIsLive(false);
      }

      if (result === 'incorrect' && showCorrectAnswer) {
        requestAnimationFrame(() => {
          setValue(correctAnswer);
        });
      }
    }
  }, [result]);

  useEffect(() => {
    if (!window.dev()) {
      return;
    }

    const showFeedbackSubscription = pubSub.subscribe(
      `${InteractionContext.id}`,
      ({ result }) => {
        console.log(result);
        setResult(result);
        setShowFeedback(result);
      }
    );

    return () => {
      showFeedbackSubscription();
    };
  }, []);

  const sliderThumb = {
    appearance: 'none',
    width: sliderHandleSize,
    height: sliderHandleSize,
    borderRadius: '50%',
    background: sliderHandle,
    boxShadow: '0 0 3px 0 rgba(0,0,0,0.2)',
    transition: 'box-shadow 0.2s',

    cursor: 'pointer',
  } as any;

  const sliderThumbFocus = {
    appearance: 'none',
    boxShadow: `0 0 0 1px #fff, 0 0 0 3px ${getSliderHandleColor()}`,
  } as any;

  return (
    <div
      style={{
        width: '100%',
        pointerEvents: resolved === SubmitState.Resolved ? 'none' : 'auto',
      }}
    >
      {includeQuestionTextPlusImage ? (
        <div style={{ marginBottom: 15 }}>{sharedQuestionMedia}</div>
      ) : null}

      <div
        css={css({
          display: 'flex',
          justifyContent: 'space-between',
          margin: '0 0 15px 0',
        })}
      >
        <FromToField field={from} />
        <FromToField field={to} />
      </div>

      <input
        type="range"
        role="slider"
        min={sliderMin}
        aria-valuemin={sliderMin}
        max={sliderMax}
        aria-valuemax={sliderMax}
        step={sliderAnswerFormat === 'variable' ? 0.01 : undefined}
        value={_value_}
        aria-valuenow={_value_}
        css={css({
          appearance: 'none',
          width: '100%',
          height: 10,
          background: rgba,
          borderRadius: 99999,

          '&::-webkit-slider-thumb': sliderThumb,
          '&::-moz-range-thumb': sliderThumb,

          ':focus': {
            outline: 'none !important',
            border: 'none !important',
            '&::-webkit-slider-thumb': sliderThumbFocus,
            '&::-moz-range-thumb': sliderThumbFocus,
          },
        })}
        onChange={(e: any) => {
          setIsLive(true);
          setValue(e.currentTarget.value);
        }}
      />

      <div
        style={{
          margin: '20px 0',
        }}
      >
        {!!showSliderStepMarkers && (
          <div
            style={{
              width: '100%',
              padding: `0 ${35 / 2}px`,
            }}
          >
            <div
              style={{
                position: 'relative',
                height: 30,
                margin: '10px 0',
              }}
            >
              {Array.from({ length: numberOfSliderSteps + 1 })
                .map((_, index) => index)
                .map((marker: number, index: number, arr) => {
                  const distance = 100 / (arr.length - 1);
                  const markerPosition = Math.round(marker * distance);

                  return (
                    <div
                      key={`marker-${marker}`}
                      style={{
                        position: 'absolute',
                        left: `calc(${markerPosition}%)`,
                        width: 1,
                        height: 10,
                        background: sliderStepMarkerColor,
                      }}
                    />
                  );
                })}
            </div>
          </div>
        )}

        {!!includeSliderAnswerInputBox && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <div
              css={css({
                display: 'flex',
                justifyContent: 'center',
                padding: 10,
                background: sliderInputBoxBackgroundColor,
                borderRadius: 15,
                border: `1px solid ${
                  sliderInputBoxOutline
                    ? sliderInputBoxOutlineColor
                    : 'transparent'
                }`,

                boxShadow: 'none',
                transition: 'box-shadow 0.2s',
                ...(inputFieldHasFocus && {
                  boxShadow: `0 0 0 1px #fff, 0 0 0 3px ${sliderInputBoxOutlineColor}`,
                }),
              })}
            >
              <input
                key={value}
                type="number"
                aria-label="Type a value and click enter to update."
                min={sliderMin}
                max={sliderMax}
                defaultValue={_value_}
                css={css({
                  all: 'unset',
                  border: 'none !important',
                  outline: 'none !important',
                  width: 100,
                  color: SliderAnswerTextColor,
                  textAlign: 'center',
                  fontFamily: 'inherit',
                  fontSize: 16,

                  '&:focus': {
                    border: 'none !important',
                    outline: 'none !important',
                  },
                  '&:focus-visible': {
                    //
                  },

                  '::-webkit-inner-spin-button': {
                    appearance: 'none',
                    margin: 0,
                  },

                  MozAppearance: 'textfield',
                })}
                onKeyUp={e => {
                  const { value } = e.currentTarget;

                  if (e.key === 'Enter') {
                    if (value < sliderMin) {
                      e.preventDefault();
                      setValue((sliderMin as unknown) as number);
                      return;
                    }

                    if (value > sliderMax) {
                      e.preventDefault();
                      setValue((sliderMax as unknown) as number);
                      return;
                    }

                    setIsLive(true);
                    setValue((value as unknown) as number);
                  }
                }}
                onFocus={e => {
                  setInputFieldHasFocus(true);
                }}
                onBlur={e => {
                  setInputFieldHasFocus(false);

                  const localValue = e.currentTarget.value;

                  if (!localValue) {
                    e.currentTarget.value = String(value || 0);
                    return;
                  }

                  setIsLive(true);
                  setValue((localValue as unknown) as number);
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          visibility: 'hidden',
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 0.2s',

          ...((window.dev() || isLive) && {
            visibility: 'visible',
            opacity: 1,
            pointerEvents: 'auto',
          }),

          ...(resolved === SubmitState.Resolved && {
            opacity: 0,
            pointerEvents: 'none',
          }),
        }}
      >
        {cloneElement(submitBtn, {
          onClick() {
            onSubmit();
          },
        })}
      </div>

      {includeFeedback &&
        showFeedback &&
        cloneElement(feedback, {
          result,
          showAttemptNumber,
          attempt,
          numberOfAttempts,
          navigationBehaviour,
          onClose() {
            setShowFeedback(undefined);
          },
          useMediaInFeedback,
        })}
    </div>
  );
};

export default Slider;
